import DataTable from 'primevue/datatable';
import type {DataTableProps, DataTableEmits} from 'primevue/datatable';
import Column from 'primevue/column';
import type {ColumnProps, ColumnSlots} from 'primevue/column';
import type {VNode} from 'vue';
import type {EmitFn} from '@primevue/core';

// See https://stackoverflow.com/a/47058976
type PathsToStringProps<T> = T extends Array<any> | Date | string | number | boolean
    ? []
    : {
          [K in Extract<keyof T, string>]: [K, ...PathsToStringProps<T[K]>];
      }[Extract<keyof T, string>];

type Join<T extends string[]> = T extends []
    ? never
    : T extends [infer F]
    ? F
    : T extends [infer F, ...infer R]
    ? F extends string
        ? `${F}.${Join<Extract<R, string[]>>}`
        : never
    : string;

/**
 * Create PrimeVue datatable components with autocomplete and typecheck for named slots props and events
 *
 * @typeParam TData - The item type
 *
 * @exemple
 *
 * ```vue
 * <template>
 *   <TDataTable :value="tableData">
 *      <TColumn>
 *          <template #body="{data}">
 *              <!-- Typed -->
 *              <span>{{ data.title }}</span>
 *          </template>
 *      </TColumn>
 *   </TDataTable>
 * </template>
 *
 * <script setup lang="ts">
 *
 * type MyTableItem = {title: string};
 *
 * const tableData = ref<MyTableItem>([]);
 *
 * const {TColumn, TDataTable} = createDataTableComponents<MyTableItem>();
 *
 * </script>
 * ```
 */
export function createDataTableComponents<TData extends Record<string, any>>() {
    // - Column
    type TypedColumnProps<TKey extends Join<PathsToStringProps<TData>>> = Omit<ColumnProps, 'field' | 'sortField'> & {field?: TKey; sortField?: TKey};
    type BodySlotProps = Omit<ReturnType<ColumnSlots['body']>, 'data'> & {
        data: TData;
        index: number;
    };
    type TypedColumnSlots = Omit<ColumnSlots, 'body'> & {
        body: (scope: BodySlotProps) => VNode[];
    };

    type TypedColumn = new <TKey extends Join<PathsToStringProps<TData>>>(props: TypedColumnProps<TKey>) => Omit<
        typeof Column,
        '$props' | '$slots'
    > & {
        $props: TypedColumnProps<TKey>;
        $slots: TypedColumnSlots;
    };

    // - DataTable

    type OmittedTypes = 'value-change';
    type TypedDataTableEmits = Omit<DataTableEmits, OmittedTypes> & {
        /**
         * Callback to invoke after filtering, sorting, pagination and cell editing to pass the rendered value.
         *  @param {TData} value - Value displayed by the table.
         */
        'value-change': (event: TData[]) => void;
    };

    // TODO type DataTable emits

    type TypedDataTable = new (props: DataTableProps) => Omit<typeof DataTable, '$emit'> & {
        $emit: EmitFn<TypedDataTableEmits>;
    };

    return {
        TDataTable: DataTable as unknown as TypedDataTable,
        TColumn: Column as unknown as TypedColumn,
    };
}
